import { useId, useState, type CSSProperties } from "react";
import { ChevronRight, Sparkles } from "lucide-react";

export type EditorialShowcaseItem = {
  title: string;
  body: string;
};

type EditorialAccordionShowcaseProps = {
  items: EditorialShowcaseItem[];
  ariaLabel: string;
};

/**
 * One accent per question, cycled by index.
 *
 * Every value is a colour the site already uses — the brand magenta, the cyan, the
 * violet this component was built around, the amber, and the logo green — so
 * moving between questions shifts the panel's temperature without ever leaving the
 * palette. The colour is a variable rather than a fixed rule so the panel's border,
 * aura, kicker, and gradient all move together; nothing depends on the accent
 * except its own tint.
 */
const SHOWCASE_ACCENTS = [
  "#E91E8C",
  "#00D4FF",
  "#B367FF",
  "#F5A623",
  "#4AB857",
  "#FF7AC4",
] as const;

const accentFor = (index: number) => SHOWCASE_ACCENTS[index % SHOWCASE_ACCENTS.length];

export default function EditorialAccordionShowcase({ items, ariaLabel }: EditorialAccordionShowcaseProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const id = useId().replace(/:/g, "");
  const activeItem = items[activeIndex] ?? items[0];

  if (!activeItem) return null;

  return (
    <div
      className="chi-editorial-showcase"
      style={{ "--chi-showcase-accent": accentFor(activeIndex) } as CSSProperties}
    >
      <div className="chi-editorial-showcase__rail" role="tablist" aria-orientation="vertical" aria-label={ariaLabel}>
        {items.map((item, index) => {
          const isActive = index === activeIndex;
          const tabId = `${id}-tab-${index}`;

          // Its own accent as well as the panel's, so a row's chevron carries the
          // colour that question is about to turn the panel.
          return (
            <div
              className="chi-editorial-showcase__item"
              data-active={isActive || undefined}
              key={item.title}
              style={{ "--chi-showcase-accent": accentFor(index) } as CSSProperties}
            >
              <button
                id={tabId}
                type="button"
                role="tab"
                className="chi-editorial-showcase__trigger"
                aria-selected={isActive}
                aria-controls={`${id}-panel`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveIndex(index)}
                onKeyDown={(event) => {
                  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
                  event.preventDefault();
                  const nextIndex = event.key === "Home"
                    ? 0
                    : event.key === "End"
                      ? items.length - 1
                      : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
                  setActiveIndex(nextIndex);
                  requestAnimationFrame(() => document.getElementById(`${id}-tab-${nextIndex}`)?.focus());
                }}
              >
                <span>{item.title}</span>
                <ChevronRight aria-hidden="true" size={18} />
              </button>
            </div>
          );
        })}
      </div>

      <aside
        id={`${id}-panel`}
        role="tabpanel"
        aria-labelledby={`${id}-tab-${activeIndex}`}
        className="chi-editorial-showcase__preview"
        aria-live="polite"
      >
        <div className="chi-editorial-showcase__preview-aura" aria-hidden="true" />
        <div className="chi-editorial-showcase__preview-kicker">
          <Sparkles size={14} aria-hidden="true" /> Community answer <span>{String(activeIndex + 1).padStart(2, "0")}</span>
        </div>
        <p>{activeItem.body}</p>
        <div className="chi-editorial-showcase__signals" aria-label="CHI Alliance approach">
          <span>People-led</span>
          <span>Practical</span>
          <span>Connection-first</span>
        </div>
      </aside>
    </div>
  );
}
